import type { ConflictReason, LocalSale } from './types';

/**
 * Hinweise zu einem Verkauf in Klartext (U17e). Ersetzt „Hinweis vom Server: Position 1: …“: Die Hinweise werden je Grund
 * gebündelt und nennen die Bezeichnungen der Artikel statt „Position n“. Die Texte sprechen von der „Basar-Leitung“, nie
 * von „Server“ oder „Veranstalter“. Rein, ohne Oberfläche.
 */

export interface SaleNotice {
  /** Stabiler Schlüssel (je Grund einer). */
  id: string;
  /** `info` gelb/neutral, `error` rot, `muted` grau (erledigt). */
  tone: 'info' | 'error' | 'muted';
  text: string;
  /** Zusatz in kleiner Schrift (z. B. die Meldung zur Ablehnung). */
  detail?: string;
}

/** Bezeichnung der Position; fehlt sie (älterer Verlauf), steht „Artikel 2“. */
function nameOf(sale: LocalSale, lineNo: number): string {
  const title = sale.lines[lineNo - 1]?.title.trim() ?? '';
  return title === '' ? `Artikel ${String(lineNo)}` : title;
}

/** „A“, „A und B“, „A, B und C“. */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} und ${names[names.length - 1] ?? ''}`;
}

const unique = (numbers: readonly number[]): number[] =>
  [...new Set(numbers)].sort((a, b) => a - b);

/** Hinweise je Grund: eine Zeile je Grund, Reihenfolge fest (wichtigste zuerst). */
export function saleNotices(sale: LocalSale): SaleNotice[] {
  const notices: SaleNotice[] = [];
  if (sale.state === 'failed') {
    notices.push({
      id: 'failed',
      tone: 'error',
      text: 'Dieser Verkauf wurde nicht übernommen. Er ist gesichert, und die Basar-Leitung sieht ihn. Bitte melden Sie kurz, was passiert ist.',
      ...(sale.failure === undefined ? {} : { detail: sale.failure.message }),
    });
  }
  const linesOf = (reason: ConflictReason): string[] =>
    unique(
      sale.conflicts.filter((entry) => entry.reason === reason).map((entry) => entry.lineNo),
    ).map((lineNo) => nameOf(sale, lineNo));
  const text: SaleNotice[] = [];
  const notReceived = linesOf('not_received');
  if (notReceived.length > 0) {
    const many = notReceived.length > 1;
    text.push({
      id: 'not_received',
      tone: 'info',
      text: `${joinNames(notReceived)} ${many ? 'waren' : 'war'} beim Verkauf noch nicht als angenommen erfasst. ${many ? 'Sie zählen' : 'Der Artikel zählt'} trotzdem für den Verkäufer; die Basar-Leitung prüft das.`,
    });
  }
  const duplicate = linesOf('duplicate');
  if (duplicate.length > 0) {
    text.push({
      id: 'duplicate',
      tone: 'info',
      text: `${joinNames(duplicate)} ${duplicate.length > 1 ? 'waren' : 'war'} schon an einer anderen Kasse verkauft. Bitte das Geld behalten; die Basar-Leitung klärt, wem es zählt.`,
    });
  }
  const unknown = linesOf('unknown_code');
  if (unknown.length > 0) {
    text.push({
      id: 'unknown_code',
      tone: 'info',
      text:
        unknown.length === 1
          ? 'Ein Code war im Basar nicht bekannt. Die Basar-Leitung ordnet ihn zu.'
          : `${String(unknown.length)} Codes waren im Basar nicht bekannt. Die Basar-Leitung ordnet sie zu.`,
    });
  }
  const deleted = linesOf('deleted');
  if (deleted.length > 0) {
    text.push({
      id: 'deleted',
      tone: 'info',
      text: `${joinNames(deleted)} ${deleted.length > 1 ? 'waren' : 'war'} vom Verkäufer gelöscht. Die Basar-Leitung klärt das.`,
    });
  }
  const mismatch = unique(sale.priceMismatches).map((lineNo) => nameOf(sale, lineNo));
  if (mismatch.length > 0) {
    text.push({
      id: 'priceMismatch',
      tone: 'info',
      text: `Bei ${joinNames(mismatch)} wich der Preis vom aktuellen Stand ab. Die Basar-Leitung sieht das.`,
    });
  }
  const promo = unique(sale.promoInvalid ?? []).map((lineNo) => nameOf(sale, lineNo));
  if (promo.length > 0) {
    text.push({
      id: 'promoInvalid',
      tone: 'info',
      text: `Die Aktion galt für ${joinNames(promo)} nicht (kein Einverständnis oder außerhalb der Zeit). Die Basar-Leitung sieht das.`,
    });
  }
  // Ein stornierter Verkauf zählt in der Abrechnung nicht: Seine Hinweise sind erledigt und nicht mehr gelb
  if (sale.voided && text.length > 0) {
    notices.push({
      id: 'voided',
      tone: 'muted',
      text: 'Storniert, die Hinweise sind damit erledigt.',
    });
    return notices;
  }
  return [...notices, ...text];
}
